/**
 * GrantBonusForm — 财务页「主动发放 BONUS」内嵌表单（ADM-FIN-S004），已接真。
 *
 * 「确认发放」走 POST /api/v1/finance/grant-bonus（financeApi.grantBonus + useMutation），body 严格
 * 按 shared GrantBonusRequest：{ user_id(UUID), amount(正整数 string ≤ 2000), reason_i18n }。
 * reason_i18n：locale→文案（1-500 字），**必含非空 `'en'` 键**（en = server 派生失败的 fallback 地板）。
 *
 * 发放原因展示语言不再由运营手选：reason 展示语言由 server 按用户最近语言（last_seen_language_per_app）在
 * **展示侧**（C 端 /me/transactions/bonus）派生；grant 写侧只存 reason_i18n 池 + 校 en 必填，不在 admin
 * 解析 / 渲染语言（dev-docs A1 §9 / #167，PR #321 merged）。故本表单移除原「目标语言手选 + locale 候选
 * 派生 + 候选空阻断」整套；用户体系 per App per user、user_id 不跨 App，无需手选 locale / 跨 App 语言仲裁。
 *
 * 用户查找卡（display_name / app_name / status / risk）保留为发放前确认上下文（确认发放对象，非 locale 用途）：
 * 点「查找」拉 GET /api/v1/users/:id，查到「当前输入 user_id」的用户（id 匹配）才 resolved，方可发放。
 * 查找有 race 守卫（codex#1）：查 A 改 B 后 A 晚回不回填（仅当解析 id 仍是当前输入时落卡）。
 * 用户卡「累计充值」total_spend：真实 per-currency 聚合（catch-up #130），经 fmtSpendByCurrency 展示
 * 「€128.40 · zł45.00」；无充值 / 源不可得 → '—'。查找失败（404 / 网络）给错误态，不假成功。
 *
 * 顶部「BONUS 发放 KPI 卡」（ADM-FIN-S004 配套）：本月发放总额 / 人次 / 平均单次走 useBonusStats
 * （GET /finance/stats/bonus，平台级），环比 delta 用 current vs previous **真实计算**（非硬编码）；
 * prev=0 时 delta 不显「—」、loading 时占位。第 4 张「用户补偿占比」恒显「—」+ title —— 待 dev-docs
 * 定 reason_category 分类语义（NOT-in-scope，见 §五），不编造占比。
 *
 * NOT-in-scope（见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）：
 * 历史记录 / 从英文翻译 → disabled + title，禁假成功 toast；
 * 「用户补偿占比」KPI（待 reason_category 枚举）→ 显「—」+ title，不编造。
 *
 * 成功按服务端真值 toast（amount / expires_at）+ invalidate ['users'] + ['finance-bonus-stats']
 * （KPI 卡即时刷新，codex#2）；失败错误 toast，表单不清空。
 */

import type { AdminUserDetailResponse, GrantBonusResponse } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo, useRef, useState } from 'react';
import { useApps, useBonusStats, useSysConfig } from '~/data/queries';
import { financeApi } from '~/hooks/use-finance';
import { usersApi } from '~/hooks/use-users';
import { fmtDate, fmtSpendByCurrency } from '~/lib/format';
import { Icons } from '~/lib/icons';
import { useAuthStore } from '~/stores/auth-store';
import { Avatar } from '~/ui/Avatar';
import { MultiLangFormRows, type MultiLangValue } from '~/ui/MultiLangFormRows';
import { toast } from '~/ui/Toast';

const PRESETS = [20, 50, 100, 200];
// 快捷原因（中文）：点击预填 reason_i18n.zh；默认原因「用户补偿」对齐 ADM-FIN-S004
const REASONS = ['用户补偿', '客服补偿', '活动奖励', '故障补偿', 'VIP 关怀'];

const NOT_IN_SCOPE_TITLE =
  '待契约（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）';

// BONUS 发放 root 专属（后端 POST /finance/grant-bonus requireRole('root')；money 路径）。前端只做 UX 门控。
const FORBIDDEN_ROLE_TITLE = '权限不足 · BONUS 发放仅超管（root）可操作（后端 requireRole 为权威）';
// 发放前必须确认查到当前用户（确认发放对象）→ 未 resolved 时按钮 disabled + 提示先查找
const LOOKUP_REQUIRED_TITLE = '请先点「查找」确认用户后再发放';

// user_id 必须是合法 UUID（server path/body 校验 z.string().uuid()）
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;

// 待 dev-docs 定 reason_category 分类语义（NOT-in-scope，见 §五）→「用户补偿占比」KPI 显「—」+ title
const COMPENSATION_SHARE_TITLE =
  '待 dev-docs 定 reason_category 分类语义（NOT-in-scope，见 docs/plans/2026-06-12-web-real-api-integration-plan.md §五）';
// KPI 加载失败：useBonusStats isError 时显「—」+ title（区分真实「本月 0」与故障，codex PR#77）
const BONUS_STATS_TITLE_ERROR = 'BONUS 发放统计加载失败 · 刷新重试（非「本月无发放」）';

/** 环比 delta（%）：prev≤0 时返 null（不显，避免 ÷0 出 Infinity / 编造增幅）。ratio 用 Number 足够（%）。 */
function pctDelta(cur: number, prev: number): number | null {
  if (prev <= 0) return null;
  return ((cur - prev) / prev) * 100;
}

/** 整数 string → 千分位（BigInt 渲染保精度；空/非法 → 原值兜底）。codex#2：>2^53 用 Number 会丢精度。 */
function fmtIntStr(s: string | undefined): string {
  if (!s) return '0';
  try {
    return BigInt(s).toLocaleString();
  } catch {
    return s;
  }
}

/** 单张 BONUS KPI 卡（real 三张：值 + 真实环比 delta；占位卡：值「—」+ title 说明 NOT-in-scope 原因）。 */
function BonusKpiCard({
  label,
  value,
  delta,
  loading,
  placeholderTitle,
}: {
  label: string;
  value: string;
  /** 环比百分比；null = 不显（prev=0 / 占位卡）；undefined = 占位卡（不算 delta）*/
  delta?: number | null;
  loading?: boolean;
  /** 占位卡 title（说明为何显「—」）*/
  placeholderTitle?: string;
}) {
  const showDelta = delta !== undefined && delta !== null;
  return (
    <div className="card stat" title={placeholderTitle} style={{ padding: 14 }}>
      <div className="stat-label">{label}</div>
      <div className="stat-value mono" style={{ fontSize: 22, marginTop: 4 }}>
        {loading ? '…' : value}
      </div>
      {showDelta ? (
        <div className={`stat-delta ${delta >= 0 ? 'delta-pos' : 'delta-neg'}`}>
          {delta >= 0 ? <Icons.IconArrowUp size={10} /> : <Icons.IconArrowDown size={10} />}
          <span className="mono">{Math.abs(delta).toFixed(1)}%</span>
        </div>
      ) : (
        // prev=0（环比无基准）/ 占位卡 → 不编造增幅，留位高度一致
        <div className="stat-delta" style={{ color: 'var(--fg-faint)' }}>
          <span className="mono">—</span>
        </div>
      )}
    </div>
  );
}

export function GrantBonusForm() {
  const { data: apps } = useApps();
  const { data: sysConfig } = useSysConfig();
  // BONUS 发放月度 KPI（平台级；data=null 时占位、prev=0 时 delta 不显、isError 时显「—」非 0）
  const {
    data: bonusStats,
    isLoading: bonusStatsLoading,
    isError: bonusStatsError,
  } = useBonusStats();
  // 写权限门控（root 专属）：非 root 禁用确认按钮 + title 标注（与 users 域 useCanGrantBonus 一致）
  const canGrant = useAuthStore((s) => s.user?.role) === 'root';

  const [userId, setUserId] = useState('');
  // 用户查找：点「查找」拉 GET /users/:id 展示用户卡（查到落 lookedUpUser，错误态由 mutation.isError 驱动）
  const [lookedUpUser, setLookedUpUser] = useState<AdminUserDetailResponse | null>(null);

  // 已确认查到「当前输入 user_id 的」用户（id 匹配）才算 resolved——发放前确认发放对象（非 locale 用途）。
  // codex#77 二轮：UUID 大小写不敏感——粘大写 UUID → server 返小写规范 id，直接 === 会误判 resolvedUser=null
  //   （表单卡死）。比较前两侧 toLowerCase 归一。
  const resolvedUser =
    lookedUpUser && lookedUpUser.id.toLowerCase() === userId.trim().toLowerCase()
      ? lookedUpUser
      : null;
  // app_id → App 名映射（用户卡「归属 App」用；DTO 自带 app_name，缺则回退 apps 列表 join，再缺回退 app_id）
  const appNameById = useMemo(() => new Map(apps.map((a) => [a.id, a.name])), [apps]);

  const [amount, setAmount] = useState(50);
  const [reasonI18n, setReasonI18n] = useState<MultiLangValue>({ zh: REASONS[0] ?? '', en: '' });
  // 成功后锁定一次发放（防重复发真实 BONUS）：服务端每次新 grant_id 不去重，同 payload 二次提交 = 重复发放。
  // 成功即记下本次结果 + 清空 user_id（同时让 canSubmit 失效），运营须显式「再发一笔」才解锁下一次。
  const [lastGrant, setLastGrant] = useState<GrantBonusResponse | null>(null);

  const userIdValid = UUID_RE.test(userId.trim());
  // codex#1：查 A 改 B 后 A 的请求晚回 → onSuccess 不能无条件回填（否则卡片显 A 的 name/app/status 配 B 的 user_id）。
  // 用 ref 跟当前输入（mutation onSuccess 闭包里的 userId 是 stale），仅当解析的 id 仍是当前输入时才落卡。
  const currentUserIdRef = useRef(userId);
  currentUserIdRef.current = userId;
  const lookupMutation = useMutation({
    mutationFn: (id: string) => usersApi.detail(id),
    onSuccess: (dto, lookedUpId) => {
      // UUID 大小写归一比较（同 resolvedUser，codex#77 二轮），仅当解析 id 仍是当前输入时才落卡
      if (lookedUpId.toLowerCase() === currentUserIdRef.current.trim().toLowerCase())
        setLookedUpUser(dto);
    },
    // 失败态由 mutation.isError 直接驱动 UI（不弹假成功 toast）
  });
  // user_id 变更：改输入即让旧用户卡 / 错误态失效（避免查 A 改 B 仍展示 A 的卡误导发放对象）。
  const onUserIdChange = (next: string) => {
    setUserId(next);
    setLookedUpUser(null);
    lookupMutation.reset();
  };

  // 必填只有 'en' —— 契约地板（`02-API契约/A1-运营财务.md` §9.1，违反报
  // FINANCE_BONUS_REASON_LOCALE_REQUIRED）。zh 仍固定展示并预填快捷原因，但不强制：
  // reason 展示语言由 server 按 last_seen_language 派生，而语言候选集排除 zh
  // （`01-通用约定/85-运营配置维度模型.md` §1.1）——zh 值锁不成展示快照，强制它只挡住合法发放。
  const requiredLangs = ['en'];
  const pinnedLangs = ['zh', 'en'];
  // 「其他语言」可选全集 = useSysConfig 真数据 enabled 语言 code（组件默认 fallback 仅 zh/en，不读 mock seed）
  const enabledLangs = sysConfig.languages.filter((l) => l.enabled).map((l) => l.language_code);

  const queryClient = useQueryClient();
  const grantMutation = useMutation({
    mutationFn: () => {
      // 仅提交非空文案（契约 record 值 min(1)，空串会被服务端拒绝）
      const reason_i18n = Object.fromEntries(
        Object.entries(reasonI18n)
          .map(([k, v]) => [k, v.trim()] as const)
          .filter(([, v]) => v.length > 0),
      );
      return financeApi.grantBonus({
        user_id: userId.trim(),
        amount: String(amount),
        reason_i18n,
      });
    },
    onSuccess: (dto) => {
      toast(
        `已向 ${dto.user_id} 发放 ${Number(dto.amount).toLocaleString()} BONUS · 有效期至 ${fmtDate(dto.expires_at)}`,
        { tone: 'pos', icon: 'IconCoin' },
      );
      // 防重复发放：记下结果 + 清空 user_id（canSubmit 立即失效，同 payload 无法二次提交）。
      // 运营须显式「再发一笔」才解锁下一次。
      setLastGrant(dto);
      setUserId('');
      // BONUS 写 C 端钱包，刷新用户列表（余额随之变化）
      queryClient.invalidateQueries({ queryKey: ['users'] });
      // codex#2：发放正是顶部 KPI 卡汇总的动作 → 同步刷新 bonus-stats，否则本月总额/人次/平均立即变 stale
      queryClient.invalidateQueries({ queryKey: ['finance-bonus-stats'] });
    },
    onError: (err) => {
      toast(`发放失败 · ${err instanceof Error ? err.message : '未知错误'}`, {
        tone: 'neg',
        icon: 'IconAlert',
      });
    },
  });

  const canSubmit =
    canGrant &&
    !lastGrant && // 成功后锁定，须显式「再发一笔」解锁（防重复发放）
    userIdValid &&
    // 必须已确认查到「当前 user_id」的用户才能发（确认发放对象）；pending 时也阻断。
    !!resolvedUser &&
    !lookupMutation.isPending &&
    amount > 0 &&
    amount <= 2000 && // 全局上限 2000（dev-docs A1 §9 / #167）；越界 server 返 FINANCE_BONUS_AMOUNT_INVALID
    requiredLangs.every((lang) => (reasonI18n[lang] ?? '').trim().length > 0) &&
    !grantMutation.isPending;

  // 显式开始下一次发放：解锁表单 + 重置金额 / 原因（user_id 已在 onSuccess 清空）
  const startNextGrant = () => {
    setLastGrant(null);
    setAmount(50);
    setReasonI18n({ zh: REASONS[0] ?? '', en: '' });
  };

  // 预览（用户端通知）：展示语言由 server 按用户最近语言派生，admin 侧预览以 en（fallback 地板）→ zh 兜底
  const previewText = reasonI18n.en || reasonI18n.zh || '';

  // 用户卡数据派生（仅「已 resolved（id 匹配当前输入）」时）。用 resolvedUser 非 lookedUpUser：
  // 取消 / 发放成功后 setUserId('') 使 resolvedUser→null，用户卡随之隐藏，避免输入已清空但卡片残留上一个用户（codex#77 对抗 review P2）。
  const cardAppName = resolvedUser
    ? (resolvedUser.app_name ??
      (resolvedUser.app_id ? (appNameById.get(resolvedUser.app_id) ?? resolvedUser.app_id) : null))
    : null;

  return (
    <div className="vstack" style={{ gap: 12 }}>
      {/* BONUS 发放 KPI 卡（本月真值；环比用 current vs previous 真实算，第 4 张占位「—」）。
          codex#77 二轮：bonus_grants 无 app_id → KPI 恒「全平台口径」、不随顶栏 App 切换 → 显式标注，
          避免单 App 视图下把别的 App 的发放误读成「本 App 本月发放」。*/}
      <div className="hstack" style={{ gap: 6, fontSize: 11.5, color: 'var(--fg-muted)' }}>
        <Icons.IconInfo size={11} style={{ color: 'var(--info)', flexShrink: 0 }} />
        <span>
          BONUS 发放统计 · <span className="strong">全平台口径</span>（不分 App，不随顶栏 App 切换）
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        <BonusKpiCard
          label="本月发放总额"
          value={bonusStatsError ? '—' : `${fmtIntStr(bonusStats?.current.totalAmount)} BONUS`}
          delta={
            !bonusStatsError && bonusStats
              ? pctDelta(
                  Number(bonusStats.current.totalAmount),
                  Number(bonusStats.previous.totalAmount),
                )
              : null
          }
          loading={bonusStatsLoading}
          placeholderTitle={bonusStatsError ? BONUS_STATS_TITLE_ERROR : undefined}
        />
        <BonusKpiCard
          label="本月发放人次"
          value={bonusStatsError ? '—' : (bonusStats?.current.grantCount ?? 0).toLocaleString()}
          delta={
            !bonusStatsError && bonusStats
              ? pctDelta(bonusStats.current.grantCount, bonusStats.previous.grantCount)
              : null
          }
          loading={bonusStatsLoading}
          placeholderTitle={bonusStatsError ? BONUS_STATS_TITLE_ERROR : undefined}
        />
        <BonusKpiCard
          label="平均单次"
          value={bonusStatsError ? '—' : `${fmtIntStr(bonusStats?.current.avgPerGrant)} BONUS`}
          delta={
            !bonusStatsError && bonusStats
              ? pctDelta(
                  Number(bonusStats.current.avgPerGrant),
                  Number(bonusStats.previous.avgPerGrant),
                )
              : null
          }
          loading={bonusStatsLoading}
          placeholderTitle={bonusStatsError ? BONUS_STATS_TITLE_ERROR : undefined}
        />
        {/* 用户补偿占比：reason_category 枚举未定 → 恒「—」+ title（不编造） */}
        <BonusKpiCard label="用户补偿占比" value="—" placeholderTitle={COMPENSATION_SHARE_TITLE} />
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)',
          gap: 24,
        }}
      >
        <div className="vstack" style={{ gap: 14 }}>
          <div>
            <div className="label" style={{ marginBottom: 6 }}>
              用户 ID
            </div>
            <div className="hstack" style={{ gap: 8 }}>
              <input
                className="input mono"
                placeholder="用户 UUID（如 0190a203-…）"
                style={{ flex: 1, fontSize: 12 }}
                value={userId}
                onChange={(e) => onUserIdChange(e.target.value)}
              />
              {/* 查找：接 GET /api/v1/users/:id（user_id 为合法 UUID 后可点）→ 展示用户卡 */}
              <button
                className="btn"
                disabled={!userIdValid || lookupMutation.isPending}
                title={userIdValid ? undefined : '请输入合法 UUID 后再查找'}
                onClick={() => lookupMutation.mutate(userId.trim())}
              >
                {lookupMutation.isPending ? '查找中…' : '查找'}
              </button>
            </div>
            {userId.trim() && !userIdValid && (
              <span
                className="hstack"
                style={{ gap: 5, marginTop: 6, fontSize: 11, color: 'var(--neg)' }}
              >
                <Icons.IconAlert size={11} style={{ flexShrink: 0 }} />
                user_id 必须是合法 UUID（服务端校验）
              </span>
            )}

            {/* 查找失败态（404 / 网络）：错误条，不假成功 */}
            {lookupMutation.isError && (
              <div
                className="hstack"
                style={{
                  gap: 6,
                  marginTop: 8,
                  padding: '8px 12px',
                  borderRadius: 6,
                  fontSize: 11.5,
                  color: 'var(--neg)',
                  background: 'var(--neg-soft)',
                  border: '1px solid var(--neg)',
                }}
              >
                <Icons.IconAlert size={12} style={{ flexShrink: 0 }} />
                查找失败 ·{' '}
                {lookupMutation.error instanceof Error
                  ? lookupMutation.error.message
                  : '用户不存在或网络异常'}
              </div>
            )}

            {/* 用户信息卡（查到用户时）：头像 / 用户名 / user_id / App / 状态 chip；累计充值接 ③ 真实 per-currency */}
            {resolvedUser && (
              <div
                className="hstack"
                style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 6,
                  gap: 10,
                  background: 'var(--bg-subtle)',
                }}
              >
                <Avatar
                  seed={resolvedUser.id}
                  size={28}
                  text={(resolvedUser.display_name ?? '?').slice(0, 1).toUpperCase()}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 500 }}>
                    {resolvedUser.display_name ?? '（无名）'} ·{' '}
                    <span className="mono" style={{ color: 'var(--fg-muted)' }}>
                      {resolvedUser.id.slice(0, 8)}…
                    </span>
                  </div>
                  <div
                    className="hstack"
                    style={{ gap: 6, fontSize: 11, color: 'var(--fg-muted)' }}
                  >
                    <span>{cardAppName ?? '未知 App'}</span>
                    <span>·</span>
                    {/* 累计充值：接 ③（catch-up #130）真实 per-currency 聚合；无充值/源不可得 → fmtSpendByCurrency 返 '—' */}
                    <span>累计充值 {fmtSpendByCurrency(resolvedUser.total_spend)}</span>
                  </div>
                </div>
                <div className="hstack" style={{ gap: 4 }}>
                  <span
                    className={`chip ${resolvedUser.status === 'BANNED' ? 'chip-neg' : 'chip-pos'}`}
                  >
                    {resolvedUser.status === 'BANNED' ? '已封禁' : '正常'}
                  </span>
                  {resolvedUser.risk_status !== 'NONE' && (
                    <span className="chip chip-warn">
                      {resolvedUser.risk_status === 'RESTRICTED' ? '受限' : '风控观察'}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="label" style={{ marginBottom: 6 }}>
              发放金额
            </div>
            <div className="hstack" style={{ gap: 8 }}>
              <input
                type="number"
                className="input mono"
                value={amount}
                min={1}
                max={2000}
                onChange={(e) =>
                  setAmount(Math.max(0, Math.min(2000, Number(e.target.value) || 0)))
                }
                style={{ width: 100, textAlign: 'right' }}
              />
              <span style={{ color: 'var(--fg-muted)', fontSize: 12 }}>BONUS</span>
              <div className="hstack" style={{ gap: 4, marginLeft: 12 }}>
                {PRESETS.map((n) => (
                  <button
                    key={n}
                    className="btn btn-sm"
                    onClick={() => setAmount(n)}
                    style={{
                      background: amount === n ? 'var(--accent-soft)' : undefined,
                      color: amount === n ? 'var(--accent)' : undefined,
                      borderColor: amount === n ? 'var(--accent)' : undefined,
                    }}
                  >
                    +{n}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <div className="label" style={{ marginBottom: 6 }}>
              快捷原因
            </div>
            <div className="hstack" style={{ gap: 4, flexWrap: 'wrap' }}>
              {REASONS.map((r) => (
                <button
                  key={r}
                  className="btn btn-sm"
                  onClick={() => setReasonI18n((prev) => ({ ...prev, zh: r }))}
                  style={{
                    fontSize: 11,
                    background: reasonI18n.zh === r ? 'var(--accent-soft)' : undefined,
                    color: reasonI18n.zh === r ? 'var(--accent)' : undefined,
                    borderColor: reasonI18n.zh === r ? 'var(--accent)' : undefined,
                  }}
                >
                  {r}
                </button>
              ))}
            </div>
          </div>

          {/* 发放原因给 C 端展示 → 契约 reason_i18n 多语言录入（必填 zh/en，en 为契约强校验地板） */}
          <MultiLangFormRows
            label="发放原因"
            multiline
            value={reasonI18n}
            onChange={setReasonI18n}
            requiredLangs={requiredLangs}
            pinnedLangs={pinnedLangs}
            availableLangs={enabledLangs}
          />
          <div style={{ fontSize: 11, color: 'var(--fg-muted)' }}>
            <span className="hstack" style={{ display: 'inline-flex', gap: 4 }}>
              <Icons.IconSparkles size={11} style={{ color: 'var(--accent)' }} />
              原因展示语言由 server 按<span className="strong">用户最近语言</span>派生（en 为
              fallback 地板）·{' '}
              {/* 从英文翻译：契约只覆盖 feedback 回复（DeepL）→ disabled + NOT-in-scope */}
              <button
                className="btn btn-ghost btn-sm"
                disabled
                title={NOT_IN_SCOPE_TITLE}
                style={{ padding: '0 4px', height: 20, cursor: 'not-allowed' }}
              >
                从英文翻译
              </button>
            </span>
          </div>
        </div>

        <div
          className="card-flat"
          style={{
            padding: 14,
            background: 'var(--bg-subtle)',
            border: '1px dashed var(--border)',
          }}
        >
          <div className="label" style={{ marginBottom: 8 }}>
            预览 · 用户端通知
          </div>
          <div className="card" style={{ padding: 14, background: 'var(--bg-elev)' }}>
            <div className="hstack" style={{ gap: 10 }}>
              <Icons.IconCoin size={18} style={{ color: 'var(--warn)' }} />
              <div>
                <div style={{ fontSize: 12.5, fontWeight: 500 }}>
                  {amount.toLocaleString()} BONUS
                </div>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 2 }}>
                  {previewText || '（请填写发放原因）'}
                </div>
              </div>
            </div>
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px dashed var(--border)',
                fontSize: 11,
                color: 'var(--fg-muted)',
              }}
            >
              有效期 30 天 · 实际到账数额 / 有效期以服务端返回为准
            </div>
          </div>
          {/* 发放成功后锁定态：展示本次结果 + 「再发一笔」显式解锁（防同 payload 重复发放） */}
          {lastGrant && (
            <div
              className="card"
              style={{
                marginTop: 12,
                padding: 12,
                background: 'var(--pos-soft)',
                borderColor: 'var(--pos)',
              }}
            >
              <div className="hstack" style={{ gap: 8, fontSize: 12, color: 'var(--fg)' }}>
                <Icons.IconCheck size={13} style={{ color: 'var(--pos)', flexShrink: 0 }} />
                <span>
                  已发放 {Number(lastGrant.amount).toLocaleString()} BONUS 给{' '}
                  <span className="mono">{lastGrant.user_id.slice(0, 8)}…</span> · 有效期至{' '}
                  {fmtDate(lastGrant.expires_at)}。表单已锁定，避免重复发放。
                </span>
              </div>
            </div>
          )}
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            {lastGrant ? (
              <button className="btn btn-accent" onClick={startNextGrant}>
                <Icons.IconCoin size={11} />
                再发一笔
              </button>
            ) : (
              <>
                <button
                  className="btn"
                  disabled={grantMutation.isPending}
                  onClick={() => {
                    setUserId('');
                    setAmount(50);
                    setReasonI18n({ zh: REASONS[0] ?? '', en: '' });
                  }}
                >
                  取消
                </button>
                <button
                  className="btn btn-accent"
                  disabled={!canSubmit}
                  title={
                    !canGrant
                      ? FORBIDDEN_ROLE_TITLE
                      : !resolvedUser && userIdValid
                        ? LOOKUP_REQUIRED_TITLE
                        : undefined
                  }
                  onClick={() => grantMutation.mutate()}
                >
                  <Icons.IconCoin size={11} />
                  确认发放
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
